import type { ReactNode } from 'react'
import { LogOut } from 'lucide-react'
import { useAuth } from 'react-oidc-context'
import { Navigate, Route, Routes } from 'react-router'
import { ApiEnvironmentBadge } from '@/components/ApiEnvironmentBadge'
import { AppNavigation } from '@/components/AppNavigation'
import { CockpitDetailPage } from '@/features/cockpit/CockpitDetailPage'
import { CockpitOverviewPage } from '@/features/cockpit/CockpitOverviewPage'
import { cockpitPageDefinitions } from '@/features/cockpit/cockpitData'
import { ApprovedFunnelsPage } from '@/features/funnels/ApprovedFunnelsPage'
import { CollectionKeywordDetailPage } from '@/features/collectionKeywords/CollectionKeywordDetailPage'
import { CollectionKeywordsPage } from '@/features/collectionKeywords/CollectionKeywordsPage'
import { KeywordAnalysisPage } from '@/features/keywordAnalysis/KeywordAnalysisPage'
import { KeywordProducttypeDetailPage } from '@/features/keywordAnalysis/KeywordProducttypeDetailPage'
import { KeywordCoveragePage } from '@/features/keywordCoverage/KeywordCoveragePage'
import { KeywordTrafficCoveragePage } from '@/features/keywordTrafficCoverage/KeywordTrafficCoveragePage'
import { OrdersPage } from '@/features/orders/OrdersPage'
import { SalesAnalysisPage } from '@/features/sales/SalesAnalysisPage'
import { StatsPage } from '@/features/stats/StatsPage'
import { TemuProducttypesPage } from '@/features/temuProducttypes/TemuProducttypesPage'
import { TopicsPage } from '@/features/topics/TopicsPage'

export function App(): ReactNode {
  const auth = useAuth()
  const username = auth.user?.profile.preferred_username ?? 'Unbekannt'

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800">
        <div className="mx-auto max-w-7xl px-4 py-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              Weltmaschine Insights
            </span>

            <ApiEnvironmentBadge />

            <div className="ml-auto flex items-center gap-4 text-sm">
              <span className="text-slate-600 dark:text-slate-400">{username}</span>
              <button
                type="button"
                onClick={() => {
                  void auth.signoutRedirect()
                }}
                className="inline-flex items-center gap-1.5 text-slate-500 underline-offset-2 transition hover:text-slate-900 hover:underline dark:text-slate-400 dark:hover:text-slate-100"
              >
                <LogOut aria-hidden="true" size={15} strokeWidth={1.8} />
                Abmelden
              </button>
            </div>
          </div>

          <AppNavigation />
        </div>
      </header>

      <main>
        <Routes>
          <Route index element={<CockpitOverviewPage />} />
          <Route path="stats" element={<StatsPage />} />
          <Route
            path="margin"
            element={<CockpitDetailPage definition={cockpitPageDefinitions.margin} />}
          />
          <Route
            path="trends"
            element={<CockpitDetailPage definition={cockpitPageDefinitions.trends} />}
          />
          <Route
            path="weather"
            element={<CockpitDetailPage definition={cockpitPageDefinitions.weather} />}
          />
          <Route
            path="producttypes"
            element={<CockpitDetailPage definition={cockpitPageDefinitions.producttypes} />}
          />
          <Route
            path="content"
            element={<CockpitDetailPage definition={cockpitPageDefinitions.content} />}
          />
          <Route path="topics" element={<TopicsPage />} />
          <Route path="topics/approved" element={<ApprovedFunnelsPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="sales" element={<SalesAnalysisPage />} />
          <Route path="temu-categories" element={<TemuProducttypesPage />} />
          <Route
            path="producttype-keywords"
            element={<Navigate to="/producttype-keywords/analysis" replace />}
          />
          <Route path="producttype-keywords/analysis" element={<KeywordAnalysisPage />} />
          <Route path="producttype-keywords/coverage" element={<KeywordCoveragePage />} />
          <Route path="producttype-keywords/traffic" element={<KeywordTrafficCoveragePage />} />
          <Route
            path="producttype-keywords/producttype/:producttypeKey"
            element={<KeywordProducttypeDetailPage />}
          />
          <Route
            path="collection-keywords"
            element={<Navigate to="/collection-keywords/analysis" replace />}
          />
          <Route
            path="collection-keywords/analysis"
            element={<CollectionKeywordsPage view="analysis" />}
          />
          <Route
            path="collection-keywords/coverage"
            element={<CollectionKeywordsPage view="coverage" />}
          />
          <Route
            path="collection-keywords/traffic"
            element={<CollectionKeywordsPage view="traffic" />}
          />
          <Route
            path="collection-keywords/collection/:collectionId"
            element={<CollectionKeywordDetailPage />}
          />
          <Route
            path="keywords"
            element={<Navigate to="/producttype-keywords/analysis" replace />}
          />
          <Route
            path="keywords/analysis"
            element={<Navigate to="/producttype-keywords/analysis" replace />}
          />
          <Route
            path="keywords/coverage"
            element={<Navigate to="/producttype-keywords/coverage" replace />}
          />
          <Route
            path="keywords/traffic"
            element={<Navigate to="/producttype-keywords/traffic" replace />}
          />
          <Route
            path="keyword-analysis"
            element={<Navigate to="/producttype-keywords/analysis" replace />}
          />
          <Route
            path="keyword-coverage"
            element={<Navigate to="/producttype-keywords/coverage" replace />}
          />
          <Route
            path="keyword-traffic"
            element={<Navigate to="/producttype-keywords/traffic" replace />}
          />
          <Route path="auth/callback" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  )
}
